import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gamepad2, Gift, LayoutGrid, Search, X } from "lucide-react";
import { api, img, rm, useSeo } from "../../lib/api";
import { useCart, useLang } from "../../lib/ctx";
import { TopupIcon } from "../../components/store/TopupBrand";

const GROUPS = [
  { id: "all", en: "All", bm: "Semua", icon: LayoutGrid },
  { id: "games", en: "Games", bm: "Game", icon: Gamepad2 },
  { id: "vouchers", en: "Gift Cards", bm: "Kad Hadiah", icon: Gift },
];

export default function GameTopUp() {
  const { t, lang } = useLang();
  const [grp, setGrp] = useState("all");
  const { addGame } = useCart();
  const [games, setGames] = useState(null);
  const [sel, setSel] = useState(null);
  const [den, setDen] = useState(null);
  const [pid, setPid] = useState("");
  const [sid, setSid] = useState("");
  const [q, setQ] = useState("");
  useSeo(t("topup"), "Digital top-up in Malaysia: Mobile Legends, PUBG Mobile, Free Fire, Genshin, Steam, Roblox and gift cards with DDVINTAGE CO.");
  useEffect(() => {
    const want = new URLSearchParams(window.location.search).get("item");
    api.get("/games").then((r) => { setGames(r.data); setSel(r.data.find((g) => g.slug === want) || r.data[0] || null); });
  }, []);
  const add = () => {
    if (!den || !pid.trim()) return toast.error(`${t("chooseDenom")} + ${t("playerId")}`);
    if (sel.requires_server_id && !sid.trim()) return toast.error(t("serverId"));
    addGame({ game_product_id: den.id, title: `${sel.name} — ${den.name}`, price: den.price, player_id: pid.trim(), server_id: sid.trim() });
    setDen(null);
  };
  const needle = q.trim().toLowerCase();
  const list = (games || []).filter((g) => (grp === "all" || g.group === grp) && (!needle || g.name.toLowerCase().includes(needle)));
  const idLabel = sel?.group === "vouchers" ? (lang === "bm" ? "Emel penerima" : "Recipient email") : t("playerId");
  return (
    <div data-testid="game-topup-page">
      <section className="keep-dark relative overflow-hidden">
        <div className="sp-header absolute inset-0" />
        <img src="/images/gaming1.jpg" alt="Mobile gaming" className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-multiply" />
        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-8 lg:px-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/90">Digital</p>
          <h1 className="mt-2 text-4xl font-extrabold sm:text-5xl">{t("topupTitle")}</h1>
          <p className="mt-3 max-w-2xl text-white/90">{t("topupSub")}</p>
        </div>
      </section>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8 lg:px-12">
        <div className="mb-6 flex gap-2 overflow-x-auto" data-testid="topup-group-tabs">{GROUPS.map((gr) => (
          <button key={gr.id} onClick={() => { setGrp(gr.id); setSel((games || []).find((g) => gr.id === "all" || g.group === gr.id) || null); setDen(null); }} data-testid={`topup-group-${gr.id}`}
            className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200 ${grp === gr.id ? "border-[#ee4d2d] bg-[#ee4d2d] text-white" : "border-[#e5e5e5] bg-white text-[#333] hover:border-[#ee4d2d] hover:text-[#ee4d2d]"}`}>
            <gr.icon className="h-4 w-4" />{lang === "bm" ? gr.bm : gr.en}</button>))}
        </div>
        {games === null ? <p className="text-zinc-500">Loading…</p> : !games.length ? <p className="text-zinc-400" data-testid="no-games">No game top-ups available right now — no supplier is connected.</p> : (
          <div className="grid gap-8 lg:grid-cols-[340px_1fr]">
            <div>
            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#999]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "bm" ? "Cari game atau kad hadiah…" : "Search games or gift cards…"} className="field w-full !pl-9 !pr-9" data-testid="topup-search-input" />
              {q && <button onClick={() => setQ("")} aria-label="clear" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#999] hover:text-[#ee4d2d]" data-testid="topup-search-clear"><X className="h-4 w-4" /></button>}
            </div>
            {!list.length && <p className="py-6 text-sm text-[#888]" data-testid="topup-search-empty">{lang === "bm" ? `Tiada hasil untuk "${q}"` : `No results for "${q}"`}</p>}
            <div className="grid max-h-[70vh] grid-cols-2 content-start gap-2 overflow-y-auto pr-1 sm:grid-cols-3 lg:grid-cols-1" data-testid="topup-game-list">{list.map((g) => (
              <button key={g.slug} onClick={() => { setSel(g); setDen(null); }} data-testid={`game-${g.slug}`}
                className={`flex w-full items-center gap-3 rounded-sm border bg-white p-2.5 text-left shadow-sm transition-colors duration-200 ${sel?.slug === g.slug ? "border-[#ee4d2d] ring-1 ring-[#ee4d2d]" : "border-[#eee] hover:border-[#ee4d2d]"}`}>
                <TopupIcon slug={g.slug} name={g.name} /><span className="text-sm font-semibold text-[#222]">{g.name}</span>
              </button>))}
            </div>
            </div>
            {sel && (
              <div className="rounded-sm bg-white p-5 shadow-sm sm:p-8">
                <h2 className="mb-5 text-xl font-extrabold text-[#222]" data-testid="topup-selected-name">{sel.name}</h2>
                {sel.sandbox && <p className="mb-6 border border-amber-500/40 bg-amber-500/10 p-3 font-mono text-xs text-amber-700" data-testid="topup-sandbox-banner">{t("sandbox")}</p>}
                <p className="eyebrow mb-3">1 · {idLabel}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input value={pid} onChange={(e) => setPid(e.target.value)} placeholder={idLabel} className="field" data-testid="topup-player-id-input" />
                  {sel.requires_server_id && <input value={sid} onChange={(e) => setSid(e.target.value)} placeholder={t("serverId")} className="field" data-testid="topup-server-id-input" />}
                </div>
                <p className="eyebrow mb-3 mt-8">2 · {t("chooseDenom")}</p>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">{sel.denominations.map((d) => (
                  <button key={d.id} onClick={() => setDen(d)} data-testid={`denom-${d.id}`} className={`rounded-sm border p-4 text-left transition-colors duration-200 ${den?.id === d.id ? "border-[#ee4d2d] bg-[#fff1ee]" : "border-[#eee] hover:border-[#ee4d2d]"}`}>
                    <p className="font-semibold text-[#222]">{d.name}</p><p className="mt-1 font-bold text-[#ee4d2d]">{rm(d.price)}</p>
                  </button>))}
                </div>
                <button onClick={add} className="btn-gold mt-8" data-testid="topup-add-to-cart-btn">{t("addToCart")}</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
